/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:40:59AM]
Permissions to update outside the main branch: [false]
*/

import {
    Filter,
    getFilterElementsDefault,
    parseToInt,
    toDisplayRoleName,
    toDisplayRoleType,
    useErrorState,
    useWorkplaceState,
    WorkplaceBtnActions,
    WorkplaceBtnDelete,
} from '@finbeat/react-toolkit';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getProps } from 'axios/propApi';
import {
    addRole,
    changeRole,
    deleteRole,
    getRolesExcel,
    getRolesList,
    getRoleTypes,
} from 'axios/roleApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import {
    RoleChange,
    RoleFilter as RoleFilterModel,
    RoleItem,
    RoleTableItem,
    RoleType,
} from 'models/api/roles';
import { useCallback, useState } from 'react';
import { FormatterProps } from 'react-data-grid';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { checkboxFormatter } from 'utils/columnFormatters';
import RoleAddButton from './RoleAddButton';
import RoleChangeModal from './RoleChangeModal';
import RoleCopyBtn from './RoleCopyBtn';
import RoleFilter from './RoleFilter';
import RoleResetCacheButton from './RoleResetCacheButton';

type RolesFormatterProps = FormatterProps<RoleTableItem, RoleTableItem[]>;

const Roles = () => {
    const { t } = useTranslation();
    const api = useApi();
    const queryClient = useQueryClient();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const { handleRequestError } = useErrorState();
    const { hasRole } = useAuth();

    const [roleTypes, setRoleTypes] = useState<RoleType[]>([]);

    const getRoleTypesQuery = useQuery(['roleTypes'], () => getRoleTypes(api), {
        onSuccess: (data) => {
            setRoleTypes(data);
        },
        onError: (err) => {
            handleRequestError(err);
        },
    });

    const rolesFormatter = useCallback(
        ({ row: { roles } }: RolesFormatterProps) => (
            <span>{roles?.map((r) => toDisplayRoleName(r.name, t)).join(', ')}</span>
        ),
        []
    );

    const actionsFormatter = useCallback(
        ({ row }: RolesFormatterProps) => (
            <WorkplaceBtnActions>
                {hasRole('rolesDelete') && (
                    <WorkplaceBtnDelete key={`delete-btn-${row.id}`} id={row.id} />
                )}
                {hasRole('rolesCreate') && (
                    <RoleCopyBtn
                        key={`copy-btn-${row.id}`}
                        id={row.id}
                        roleItem={row}
                        fetchPropsQueryFn={(id) => getProps(api, 'Role', id)}
                    />
                )}
            </WorkplaceBtnActions>
        ),
        []
    );

    return (
        <BOWorkplace<RoleTableItem, RoleTableItem, Filter<RoleFilterModel>, RoleChange, RoleItem>
            testId="roles"
            pageTitle="BackOfficeRoles"
            filterParams={{
                name: '',
                displayName: '',
                dsc: '',
                isActive: { value: null, parse: (val) => val.toLowerCase() === 'true' },
                typeId: { value: null, parse: (val) => parseToInt(val) },
                showTechnical: false,
            }}
            initColumns={[
                { key: 'id', name: <Trans i18nKey="BackOfficeId" /> },
                { key: 'name', name: <Trans i18nKey="BackOfficeCode" /> },
                {
                    key: 'displayName',
                    name: <Trans i18nKey="BackOfficeName" />,
                },
                { key: 'dsc', name: <Trans i18nKey="BackOfficeDescription" /> },
                {
                    key: 'isActive',
                    name: <Trans i18nKey="BackOfficeIsActive" />,
                    cellClass: 'cell-centered',
                    headerCellClass: 'cell-centered',
                    formatter: checkboxFormatter('isActive'),
                },
                {
                    key: 'isTechnical',
                    name: <Trans i18nKey="BackOfficeIsTechnicalRole" />,
                    cellClass: 'cell-centered',
                    headerCellClass: 'cell-centered',
                    formatter: checkboxFormatter('isTechnical'),
                },
                {
                    key: 'displayType',
                    name: <Trans i18nKey="BackOfficeType" />,
                },
                {
                    key: 'roles',
                    name: <Trans i18nKey="BackOfficeRoles" />,
                    formatter: rolesFormatter,
                    sortable: false,
                },
                {
                    key: 'actions',
                    name: <FontAwesomeIcon icon={faBoltLightning} />,
                    cellClass: 'cell-centered no-change-modal',
                    headerCellClass: 'cell-centered',
                    width: 70,
                    minWidth: 70,
                    draggable: false,
                    resizable: false,
                    clickDisable: true,
                    formatter: actionsFormatter,
                },
            ]}
            storagePath="roles/columns"
            getFilterElements={getFilterElementsDefault([
                { id: 'name', name: t('BackOfficeCode') },
                { id: 'displayName', name: t('BackOfficeName') },
                { id: 'dsc', name: t('BackOfficeDescription') },
                {
                    id: 'isActive',
                    formatter: (val) => {
                        if (val === true) {
                            return t('BackOfficeFilterRoleIsActive');
                        } else if (val === false) {
                            return t('BackOfficeFilterRoleIsNotActive');
                        }
                        return val;
                    },
                },
                {
                    id: 'typeId',
                    formatter: (val) => {
                        const code = roleTypes.find((x) => x.id === val)?.code;
                        return `${t('BackOfficeType')}: ${code ? toDisplayRoleType(code, t) : val}`;
                    },
                },
                {
                    id: 'showTechnical',
                    formatter: (val) => {
                        if (val === true) {
                            return t('BackOfficeShowTechnicalRoles');
                        }
                        return '';
                    },
                },
            ])}
            showModal={hasRole('rolesGetById') ? () => setIsModalVisible(true) : undefined}
            filter={<RoleFilter roleTypes={roleTypes} getRoleTypesQuery={getRoleTypesQuery} />}
            modal={{
                component: isModalVisible && (
                    <RoleChangeModal isOpen={isModalVisible} toggle={setIsModalVisible} />
                ),
                changeMutationFn: (item) => changeRole(api, item),
                addMutationFn: (item) => addRole(api, item),
            }}
            deleteMutationFn={(id) => deleteRole(api, id)}
            fetchQueryFn={(filter) => getRolesList(api, filter)}
            buttons={[
                hasRole('rolesCreate') ? (
                    <RoleAddButton key="1" showModal={() => setIsModalVisible(true)} />
                ) : null,
                hasRole('rolesResetCache') ? <RoleResetCacheButton key="2" /> : null,
            ]}
            queryKey={['roles']}
            queryClient={queryClient}
            fetchExcelQueryFn={(filter) => getRolesExcel(api, filter)}
            showExcelBtn={hasRole('rolesExcel')}
        />
    );
};

const useRoleWorkplaceState = () =>
    useWorkplaceState<RoleTableItem, RoleTableItem, RoleChange, RoleItem>();

export default Roles;
export { useRoleWorkplaceState };
